import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';
import type {
  InvoicePrintSnapshot,
  InvoicePrintSnapshotIssue,
} from '../data/invoicePrintSnapshot';

/**
 * Append-only archive of invoice documents as issued.
 *
 * Deliberately exposes no update or delete: the value of the archive is that a
 * reprint reproduces what the customer actually received, so rows must not be
 * editable after the fact. Re-issuing appends a new version.
 */
export function createInvoicePrintSnapshotService(client: AxiosInstance) {
  return {
    /**
     * Freeze the rendered document for an invoice. Server assigns `version`
     * (next for this invoice) and `contentHash` (SHA-256 of the HTML).
     */
    issue: async (payload: InvoicePrintSnapshotIssue): Promise<InvoicePrintSnapshot> => {
      const res = await client.post<ObjectResponse<InvoicePrintSnapshot>>(
        '/InvoicePrintSnapshot/issue',
        payload,
      );
      return unwrap(res.data) ?? {};
    },

    /** Latest issued document for an invoice; empty object when never issued. */
    latest: async (invoiceId: string): Promise<InvoicePrintSnapshot> => {
      const res = await client.post<ObjectResponse<InvoicePrintSnapshot>>(
        '/InvoicePrintSnapshot/latest',
        {},
        { params: { invoiceId } },
      );
      return unwrap(res.data) ?? {};
    },

    /** Every issue/reissue of an invoice, newest first. */
    history: async (invoiceId: string): Promise<InvoicePrintSnapshot[]> => {
      const res = await client.post<ObjectResponse<InvoicePrintSnapshot[]>>(
        '/InvoicePrintSnapshot/history',
        {},
        { params: { invoiceId } },
      );
      return unwrapItems(res.data);
    },
  };
}

export type InvoicePrintSnapshotService = ReturnType<typeof createInvoicePrintSnapshotService>;
